import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const [notificaciones, setNotificaciones] = useState([]);
  const [noLeidasCount, setNoLeidasCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Cargar conteo inicial y polling cada 30 segundos
  useEffect(() => {
    loadNoLeidasCount();
    
    const interval = setInterval(() => {
      loadNoLeidasCount();
    }, 30000); // Poll cada 30 segundos

    return () => clearInterval(interval);
  }, []);

  const loadNoLeidasCount = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notificaciones/no-leidas/count`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setNoLeidasCount(data.count);
      }
      // Si es 401, no loguear error - sesión expirada es esperado
    } catch (error) {
      // Solo loguear errores de red, no 401s
      if (error.name !== 'AbortError') {
        console.error('Error loading notifications count:', error);
      }
    }
  };

  const loadNotificaciones = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/notificaciones?limit=20`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotificaciones(data);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      loadNotificaciones();
    }
    setIsOpen(!isOpen);
  };

  const marcarLeida = async (notificacionId, expedienteId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/notificaciones/${notificacionId}/marcar-leida`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        // Actualizar estado local
        setNotificaciones(prev =>
          prev.map(n => n.id === notificacionId ? { ...n, leida: true } : n)
        );
        setNoLeidasCount(prev => Math.max(0, prev - 1));
        
        // Navegar al expediente
        setIsOpen(false);
        navigate(`/expediente/${expedienteId}`);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/notificaciones/marcar-todas-leidas`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
        setNoLeidasCount(0);
        toast.success('Todas las notificaciones marcadas como leídas');
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Error al marcar notificaciones');
    }
  };

  const formatTiempo = (created_at) => {
    const ahora = new Date();
    const fecha = new Date(created_at);
    const diff = Math.floor((ahora - fecha) / 1000); // diferencia en segundos

    if (diff < 60) return 'Hace un momento';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`;
    
    return fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón del badge */}
      <button
        onClick={handleToggleDropdown}
        className="relative p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-800"
      >
        <Bell className="w-6 h-6" />
        {noLeidasCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {noLeidasCount > 99 ? '99+' : noLeidasCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white">Notificaciones</h3>
            <div className="flex items-center gap-2">
              {noLeidasCount > 0 && (
                <button
                  onClick={marcarTodasLeidas}
                  className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  title="Marcar todas como leídas"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Lista de notificaciones */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
                <p className="mt-2 text-sm">Cargando...</p>
              </div>
            ) : notificaciones.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No hay notificaciones</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {notificaciones.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => marcarLeida(notif.id, notif.expediente_id)}
                    className={`p-4 cursor-pointer transition-colors ${
                      notif.leida
                        ? 'bg-gray-800/50 hover:bg-gray-800'
                        : 'bg-blue-900/20 hover:bg-blue-900/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {!notif.leida && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white mb-1">
                          {notif.titulo}
                        </p>
                        <p className="text-xs text-gray-400 mb-2 line-clamp-2">
                          {notif.mensaje}
                        </p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            Exp. {notif.expediente_numero}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatTiempo(notif.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer - SIEMPRE visible */}
          <div className="p-3 border-t border-gray-700">
            <Button
              onClick={() => {
                setIsOpen(false);
                navigate('/gerente/notificaciones');
              }}
              variant="ghost"
              className="w-full text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 text-sm"
            >
              Ver todas las notificaciones →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
